import React, { useState } from 'react';
import classNames from 'classnames';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import Logo from '@/components/Helpers/Logo';
import Submit from '@/components/Forms/Submit';
import { withErrorBoundary } from '@/utils/errors';
import styles from './CF360Login.module.scss';

// ClientWave360 login, served from the agency's own custom domain as well as from the
// tenant host. Branding (logo, company name, colors) comes from the public
// /v1/custom_domains/branding endpoint and falls back to the ClientWave360 mark when the
// host isn't a verified custom domain. Login is email-only on this tenant: the username is
// generated at signup and users never see it.
const CF360Login = ({
  branding,
  login,
  password,
  passwordVisible,
  onChange,
  onSubmit,
  onTogglePassword,
  hasError,
  twoFactorForm
}) => {
  // A broken agency logo (deleted from S3, bad URL) falls back to the ClientWave360 mark
  // rather than leaving an empty frame.
  const [logoBroken, setLogoBroken] = useState(false);

  // `branding` is only passed in when /v1/custom_domains/branding confirmed the host is a
  // verified custom domain, so its presence is the "agency-branded" signal.
  const isBranded = Boolean(branding);
  const companyName = branding?.company_name || 'ClientWave360';
  const logoUrl = logoBroken ? null : branding?.logo_url;

  // --primary themes the module and .btn styles, --primary-color is what Bulma's own
  // input hover/focus rules read, and --accent is what the global :focus-visible outline
  // (styles/tailwind.css) reads — so one style hook covers all three systems and no
  // ClientWave green can leak onto an agency's domain.
  const brandStyle = branding?.color_1
    ? {
        '--primary': branding.color_1,
        '--primary-light': branding.color_2 || branding.color_1,
        '--primary-color': branding.color_1,
        '--accent': branding.color_1
      }
    : undefined;

  return (
    <div className={styles.page} style={brandStyle}>
      <header className={styles.header}>
        <div className={styles.headerInner}>
          {/* A logo replaces the company name entirely; the name only renders when the
              agency has no usable logo. The ClientWave360 mark never appears on an
              agency's own domain — a branded host without a logo gets the name alone. */}
          {logoUrl ? (
            <img src={logoUrl} alt={companyName} className={styles.headerLogo} onError={() => setLogoBroken(true)} />
          ) : !isBranded ? (
            // Wrapper class constrains the <img> Logo renders: base.scss has
            // `img { height: auto }`, which beats Logo's own height attribute.
            <span className={styles.headerLogoFallback}>
              <Logo />
            </span>
          ) : (
            <span className={styles.brandName}>{companyName}</span>
          )}
        </div>
      </header>

      <main className={styles.main}>
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4 }}
          className={styles.wrapper}
        >
          <div className={styles.card}>
            {logoUrl ? (
              <img src={logoUrl} alt={companyName} className={styles.cardLogo} onError={() => setLogoBroken(true)} />
            ) : !isBranded ? (
              <span className={styles.cardLogoFallback}>
                <Logo />
              </span>
            ) : null}
            <h1 className={styles.title}>Welcome back</h1>
            <p className={styles.subtitle}>Sign in to your {companyName} account</p>

            {twoFactorForm || (
              <form name="formLogin">
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="cf360-login-email">
                    Email Address
                  </label>
                  <div className={styles.inputWrapper}>
                    <Mail width={20} height={20} className={styles.inputIcon} />
                    <input
                      id="cf360-login-email"
                      name="login"
                      type="email"
                      autoComplete="email"
                      placeholder="you@example.com"
                      data-validate='["required"]'
                      value={login}
                      onChange={onChange}
                      className={classNames(styles.input, { [styles.hasError]: hasError('login') })}
                    />
                  </div>
                  {hasError('login') && <span className={styles.error}>Field is required</span>}
                </div>

                <div className={styles.field}>
                  <label className={styles.label} htmlFor="cf360-login-password">
                    Password
                  </label>
                  <div className={styles.inputWrapper}>
                    <Lock width={20} height={20} className={styles.inputIcon} />
                    <input
                      id="cf360-login-password"
                      name="password"
                      type={passwordVisible ? 'text' : 'password'}
                      autoComplete="current-password"
                      placeholder="Password"
                      data-validate='["required"]'
                      value={password}
                      onChange={onChange}
                      className={classNames(styles.input, styles.inputWithToggle, {
                        [styles.hasError]: hasError('password')
                      })}
                    />
                    <button
                      type="button"
                      aria-label={passwordVisible ? 'Hide password' : 'Show password'}
                      onClick={onTogglePassword}
                      className={styles.toggleVisibility}
                    >
                      {passwordVisible ? <EyeOff width={20} height={20} /> : <Eye width={20} height={20} />}
                    </button>
                  </div>
                  {hasError('password') && <span className={styles.error}>Field is required</span>}
                </div>

                <Submit label="Sign in" className={styles.submit} onSubmit={onSubmit} />

                <Link href="/reset-password" className={styles.forgot}>
                  Forgot your password?
                </Link>
              </form>
            )}
          </div>

          <p className={styles.footer}>
            © {new Date().getFullYear()} {companyName}
          </p>
        </motion.div>
      </main>
    </div>
  );
};

export default withErrorBoundary(CF360Login);
